/**
 * Authentication Context für die Anwendung
 * Verwaltet den Benutzer-Login-Status und bietet Auth-Funktionen
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { User } from "../types/api-types";
import { apiClient, clearAuthToken } from "../services/apiClient";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<User>;
  updateCurrentUser: (user: User) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const {
        getAuthToken,
        getRefreshToken,
        ensureTokenIsSet,
        refreshAuthToken,
      } = await import("../services/apiClient");

      const token = getAuthToken();
      const refreshToken = getRefreshToken();
      console.log("🔍 AuthContext: Checking auth tokens:", {
        accessToken: token ? "Token exists" : "No token found",
        refreshToken: refreshToken ? "Token exists" : "No refresh token found",
      });

      const restoreUserFromRefreshToken = async (): Promise<boolean> => {
        if (!refreshToken) {
          return false;
        }

        console.log("🔄 AuthContext: Attempting token refresh...");
        const refreshSuccess = await refreshAuthToken();

        if (!refreshSuccess) {
          return false;
        }

        console.log("📡 AuthContext: Calling getCurrentUser after refresh...");
        const currentUser = await apiClient.getCurrentUser();
        console.log("✅ AuthContext: Auth check successful after refresh");
        setUser(currentUser);
        return true;
      };

      if (token) {
        try {
          // Stelle sicher, dass beide Tokens im API Client gesetzt sind
          console.log("🔄 AuthContext: Re-setting tokens in API client");
          const tokenSet = ensureTokenIsSet();
          console.log(
            "🔍 AuthContext: Tokens set result:",
            tokenSet ? "SUCCESS" : "FAILED"
          );

          if (!tokenSet) {
            console.error("❌ AuthContext: Failed to set tokens in API client");
            setLoading(false);
            return;
          }

          console.log("📡 AuthContext: Calling getCurrentUser...");
          const currentUser = await apiClient.getCurrentUser();
          console.log("✅ AuthContext: Auth check successful");
          setUser(currentUser);
        } catch (error: any) {
          console.error("❌ AuthContext: Auth check failed:", error);

          // Versuche Token zu refreshen, wenn verfügbar
          if (refreshToken && error.message?.includes("Invalid token")) {
            try {
              const restored = await restoreUserFromRefreshToken();
              if (restored) {
                setLoading(false);
                return;
              }
            } catch (retryError) {
              console.error(
                "❌ AuthContext: Retry after refresh failed:",
                retryError
              );
            }
          }

          // Bei Fehler: Tokens löschen
          const { clearAuthToken } = await import("../services/apiClient");
          clearAuthToken();
        }
      } else if (refreshToken) {
        try {
          const restored = await restoreUserFromRefreshToken();
          if (!restored) {
            clearAuthToken();
          }
        } catch (error) {
          console.error("❌ AuthContext: Refresh-only auth check failed:", error);
          clearAuthToken();
        }
      } else {
        console.log("ℹ️ AuthContext: No tokens found, user needs to login");
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      console.log("🔐 AuthContext: Attempting login with:", { username });
      const response = await apiClient.login({ username, password });
      console.log("✅ AuthContext: Login response received");

      if (!response || !response.tokens || !response.tokens.accessToken) {
        console.error(
          "❌ AuthContext: Invalid login response structure:",
          response
        );
        throw new Error("Ungültiges Response-Format vom Server");
      }

      console.log("🔑 AuthContext: Setting tokens and user...");
      // Verwende neue setAuthTokens Funktion für beide Tokens
      const { setAuthTokens } = await import("../services/apiClient");
      setAuthTokens(
        response.tokens.accessToken,
        response.tokens.refreshToken || ""
      );
      setUser(response.user);
      console.log("✅ AuthContext: Login completed successfully");
    } catch (error: any) {
      console.error("❌ AuthContext: Login failed:", error);
      throw error;
    }
  };

  const logout = () => {
    console.log("🚪 AuthContext: Logging out user");
    apiClient.logout().catch(console.error);
    clearAuthToken();
    setUser(null);
    console.log("✅ AuthContext: Logout completed");
  };

  const refreshUser = useCallback(async (): Promise<User> => {
    const currentUser = await apiClient.getCurrentUser();
    setUser(currentUser);
    return currentUser;
  }, []);

  const updateCurrentUser = useCallback((nextUser: User) => {
    setUser(nextUser);
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    updateCurrentUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
